import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { MenuItem } from 'primeng/api';
import { combineLatest, filter } from 'rxjs';
import { isPresent } from 'src/app/core/utils/isPresent';
import { GetDocumentTypeDto } from 'src/app/modules/doc-types/models/doc-types.model';
import { fromDocTypes } from 'src/app/modules/doc-types/store/selectors';
import { DocumentProcessingPagedReviewResponse } from 'src/app/modules/documents/services/document-processing-api.service';
import { AppButtonSize, AppButtonType } from 'src/app/shared/shared.model';
import { LanguageDto } from 'src/app/shared/services/languages-api.service';
import { selectContentLanguages } from 'src/app/shared/store/selectors';
import {
  AnalyzeClauseVarianceResponseDto,
  WorkspaceDetailsDto,
  WorkspaceDocumentDto,
} from '../../models/workspaces.model';
import { ClauseVarianceAnalysisActions } from '../../store/actions';
import {
  selectAnalyzingClauseVariance,
  selectAnalyzingClauseVarianceResponse,
  selectLoadingReviewData,
  selectLoadingReviewDataComplete,
  selectNonReferenceDocuments,
  selectPagedReviewDataByDocumentIdMap,
  selectReferenceDocument,
  selectReviewDoneWithErrors,
  selectReviewedPagesByDocumentIdMap,
  selectTotalReviewPagesAllDocuments,
  selectTotalReviewPagesByDocumentIdMap,
  selectTotalReviewedPagesAllDocuments,
  selectWorkspaceDetails,
} from '../../store/selectors';
import {
  ClauseVarianceExportType,
  ClauseVariancePdfContext,
} from './clause-variance-analysis.helper.models';
import { ClauseVarianceAnalysisHelper } from './clause-variance-analysis.helper';
import { ClauseVarianceExportStateService } from './services/clause-variance-export-state.service';

@UntilDestroy()
@Component({
  selector: 'app-clause-variance-analysis',
  templateUrl: './clause-variance-analysis.component.html',
  styleUrl: './clause-variance-analysis.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClauseVarianceAnalysisComponent implements OnInit {
  private static readonly EXPORT_NOTIFICATION_DELAY_MS = 2000;

  public btnTypes = AppButtonType;
  public btnSizes = AppButtonSize;
  public isAnalyzingClauseVarianceLoading$ = this.store$.select(selectAnalyzingClauseVariance);
  public isLoadingReviewData$ = this.store$.select(selectLoadingReviewData);
  public exportMenuItems: MenuItem[] = [];
  public isExporting = false;
  public isExportNotificationVisible = false;
  public workspaceDetails: WorkspaceDetailsDto | null = null;
  public selectedReferenceDocument: WorkspaceDocumentDto | null = null;
  public nonReferenceDocuments: WorkspaceDocumentDto[] = [];
  private analyzingClauseVarianceResponse: AnalyzeClauseVarianceResponseDto | null = null;
  private pagedReviewDataByDocumentId: Record<string, DocumentProcessingPagedReviewResponse> = {};
  private reviewedPagesByDocumentId: Record<string, number> = {};
  private totalPagesByDocumentId: Record<string, number> = {};
  private contentLanguages: LanguageDto[] = [];
  private docTypes: GetDocumentTypeDto[] = [];
  private totalReviewPagesAllDocuments = 0;
  private totalReviewedPagesAllDocuments = 0;
  private loadingReviewDataComplete = false;
  private reviewDoneWithErrors = false;
  private lastAnalyzeRequestKey: string | null = null;
  private exportNotificationTimerId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly store$: Store,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly translocoService: TranslocoService,
    private readonly clauseVarianceExportStateService: ClauseVarianceExportStateService,
  ) {}

  ngOnInit(): void {
    this.initExportMenuItems();
    this.initSubscriptions();
    this.startInitialization();
  }

  initSubscriptions(): void {
    this.translocoService.langChanges$.pipe(untilDestroyed(this)).subscribe(() => {
      this.initExportMenuItems();
      this.cdr.markForCheck();
    });
  }

  initExportMenuItems(): void {
    this.exportMenuItems = [
      {
        label: this.translocoService.translate('workspaces.exportFullAnalysis'),
        command: () => {
          void this.exportAnalysis(ClauseVarianceExportType.Full);
        },
      },
      {
        label: this.translocoService.translate('workspaces.exportFilteredView'),
        command: () => {
          void this.exportAnalysis(ClauseVarianceExportType.Filtered);
        },
      },
    ];
  }

  startInitialization(): void {
    combineLatest([
      this.store$.select(selectWorkspaceDetails).pipe(untilDestroyed(this), filter(isPresent)),
      this.store$.select(selectReferenceDocument).pipe(untilDestroyed(this), filter(isPresent)),
      this.store$.select(selectNonReferenceDocuments).pipe(untilDestroyed(this), filter(isPresent)),
      this.store$.select(selectAnalyzingClauseVarianceResponse).pipe(untilDestroyed(this)),
      this.store$.select(selectPagedReviewDataByDocumentIdMap).pipe(untilDestroyed(this)),
      this.store$.select(selectReviewedPagesByDocumentIdMap).pipe(untilDestroyed(this)),
      this.store$.select(selectTotalReviewPagesByDocumentIdMap).pipe(untilDestroyed(this)),
      this.store$.select(selectContentLanguages).pipe(untilDestroyed(this)),
      this.store$.select(fromDocTypes.selectDocTypes).pipe(untilDestroyed(this)),
      this.store$.select(selectTotalReviewPagesAllDocuments).pipe(untilDestroyed(this)),
      this.store$.select(selectTotalReviewedPagesAllDocuments).pipe(untilDestroyed(this)),
      this.store$.select(selectLoadingReviewDataComplete).pipe(untilDestroyed(this)),
      this.store$.select(selectReviewDoneWithErrors).pipe(untilDestroyed(this)),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(
        ([
          workspaceDetails,
          selectedReferenceDocument,
          nonReferenceDocuments,
          analyzingClauseVarianceResponse,
          pagedReviewDataByDocumentId,
          reviewedPagesByDocumentId,
          totalPagesByDocumentId,
          contentLanguages,
          docTypes,
          totalReviewPagesAllDocuments,
          totalReviewedPagesAllDocuments,
          loadingReviewDataComplete,
          reviewDoneWithErrors,
        ]) => {
          this.workspaceDetails = workspaceDetails;
          this.selectedReferenceDocument = selectedReferenceDocument;
          this.nonReferenceDocuments = nonReferenceDocuments;
          this.analyzingClauseVarianceResponse = analyzingClauseVarianceResponse;
          this.pagedReviewDataByDocumentId = pagedReviewDataByDocumentId;
          this.reviewedPagesByDocumentId = reviewedPagesByDocumentId;
          this.totalPagesByDocumentId = totalPagesByDocumentId;
          this.contentLanguages = contentLanguages ?? [];
          this.docTypes = docTypes ?? [];
          this.totalReviewPagesAllDocuments = totalReviewPagesAllDocuments;
          this.totalReviewedPagesAllDocuments = totalReviewedPagesAllDocuments;
          this.loadingReviewDataComplete = loadingReviewDataComplete;
          this.reviewDoneWithErrors = reviewDoneWithErrors;

          const requestKey = `${workspaceDetails.id}-${selectedReferenceDocument.id}`;

          if (requestKey !== this.lastAnalyzeRequestKey) {
            this.lastAnalyzeRequestKey = requestKey;
            this.store$.dispatch(
              ClauseVarianceAnalysisActions.analyzeClauseVariance({
                workspaceId: workspaceDetails.id,
                referenceDocumentId: selectedReferenceDocument.id,
              }),
            );
          }

          this.cdr.markForCheck();
        },
      );
  }

  onPrimaryExportClick(): void {
    void this.exportAnalysis(ClauseVarianceExportType.Full);
  }

  async exportAnalysis(type: ClauseVarianceExportType): Promise<void> {
    if (
      this.isExporting ||
      !this.workspaceDetails ||
      !this.selectedReferenceDocument ||
      !this.analyzingClauseVarianceResponse
    ) {
      return;
    }

    this.isExporting = true;
    this.scheduleExportNotification();
    this.cdr.markForCheck();

    try {
      await this.yieldToMainThread();

      const exportContext: ClauseVariancePdfContext = {
        exportType: type,
        workspaceName: this.workspaceDetails.name,
        selectedReferenceDocument: this.selectedReferenceDocument,
        nonReferenceDocuments: this.nonReferenceDocuments,
        analyzingClauseVarianceResponse: this.analyzingClauseVarianceResponse,
        pagedReviewDataByDocumentId: this.pagedReviewDataByDocumentId,
        reviewedPagesByDocumentId: this.reviewedPagesByDocumentId,
        totalPagesByDocumentId: this.totalPagesByDocumentId,
        contentLanguages: this.contentLanguages,
        docTypes: this.docTypes,
        totalReviewPagesAllDocuments: this.totalReviewPagesAllDocuments,
        totalReviewedPagesAllDocuments: this.totalReviewedPagesAllDocuments,
        loadingReviewDataComplete: this.loadingReviewDataComplete,
        reviewDoneWithErrors: this.reviewDoneWithErrors,
        detailedExportState: this.clauseVarianceExportStateService.getDetailedExportStateSnapshot(),
      };

      await ClauseVarianceAnalysisHelper.generateAndDownloadPdf(
        this.translocoService,
        exportContext,
      );
    } catch (error) {
      console.error('Failed to export clause variance analysis report', error);
    } finally {
      this.resetExportNotification();
      this.isExporting = false;
      this.cdr.markForCheck();
    }
  }

  private scheduleExportNotification(): void {
    this.resetExportNotification();

    this.exportNotificationTimerId = setTimeout(() => {
      if (!this.isExporting) {
        return;
      }

      this.isExportNotificationVisible = true;
      this.cdr.markForCheck();
    }, ClauseVarianceAnalysisComponent.EXPORT_NOTIFICATION_DELAY_MS);
  }

  private resetExportNotification(): void {
    if (this.exportNotificationTimerId) {
      clearTimeout(this.exportNotificationTimerId);
      this.exportNotificationTimerId = null;
    }

    this.isExportNotificationVisible = false;
  }

  private yieldToMainThread(): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(resolve, 0);
    });
  }

  goToWorkspaces(): void {
    this.router.navigate(['/workspaces']);
  }

  goToWorkspaceDetails(): void {
    if (!this.workspaceDetails) {
      return;
    }

    this.router.navigate([`/workspaces/${this.workspaceDetails.id}`]);
  }
}
